// Since our formatted logs (using Pino) is injected by the start / serve scripts, regular console.log is used inside the app

/**
 * Field names that must never appear in plaintext in logs. Authentication
 * material (authorization codes, tokens) and request-borne credentials
 * (authorization headers, cookies) are replaced by the Pino `redact` option
 * wherever they appear as structured log fields.
 *
 * NOTE: Pino redaction only matches structured object fields, not values
 * interpolated into message strings (e.g. `Code: ${code}`). Call sites must
 * avoid putting sensitive values into message strings.
 */
export const SENSITIVE_LOG_FIELD_NAMES = [
  'code',
  'access_token',
  'accessToken',
  'refresh_token',
  'refreshToken',
  'id_token',
  'idToken',
  'micrositeToken',
  'authorization',
  'cookie',
] as const

/**
 * Pino `redact.paths` entries. Covers top-level occurrences, one level of
 * nesting (`*.field`), request header locations, and the `tokens` object shape
 * returned by the auth client (`authData.tokens.*`).
 */
export const SENSITIVE_LOG_REDACT_PATHS: string[] = [
  ...SENSITIVE_LOG_FIELD_NAMES,
  ...SENSITIVE_LOG_FIELD_NAMES.map(field => `*.${field}`),
  'req.headers.authorization',
  'req.headers.cookie',
  'headers.authorization',
  'headers.cookie',
  'tokens.idToken',
  'tokens.refreshToken',
  'tokens.micrositeToken',
]

type Data = Record<string, unknown>
type LogMethod = (data: Data, message: string) => void
type LogMethodOverload = (message: string) => void
type LogMethodOverloadOnlyData = (data: Data) => void
type LogMethodWithOverloads = LogMethod & LogMethodOverload & LogMethodOverloadOnlyData

type Logger = {
  level: 'debug' | 'info' | 'warn' | 'error'
  debug: LogMethodWithOverloads
  info: LogMethodWithOverloads
  warn: LogMethodWithOverloads
  error: LogMethodWithOverloads
}

const createLogMethod = (
  logFunction: (message?: unknown, ...optionalParams: unknown[]) => void
): LogMethod & LogMethodOverload & LogMethodOverloadOnlyData => {
  return ((firstParam: Data | string, secondParam?: string) => {
    if (typeof firstParam === 'string') {
      logFunction(firstParam, secondParam as unknown)
    } else if (secondParam !== undefined) {
      logFunction(secondParam, firstParam)
    } else {
      logFunction(firstParam)
    }
  }) as LogMethod & LogMethodOverload & LogMethodOverloadOnlyData
}

const logger: Logger = {
  // TODO: Handle log level
  level: 'info',
  // TODO: Handle debug level
  // debug: createLogMethod(console.debug),
  debug: () => {},
  info: createLogMethod(console.log),
  warn: createLogMethod(console.warn),
  error: createLogMethod(console.error),
}

export const log = logger
